import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

export interface KeyDTO {
  id: string;
  keyCode: string;
  doorId: string | null;
  roomId: string | null;
  roomName: string | null;
  floorId: string | null;
  floorName: string | null;
  buildingId: string | null;
  buildingName: string | null;
  vehicleId: string | null;
  vehicleNumberPlate: string | null;
  noOfKeys: number;
  separatedKeys: number | 0;
  bundles: { count: number }[] | 0;
  responsibleParty: string | null;
  isRestricted: boolean;
  handoverStatus: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface UpdateKeyModuleProps {
  show: boolean;
  onHide: () => void;
  keyData: KeyDTO | null;
  doorId: number;
  roomId: number;
  onKeyUpdated: () => void;
}

const UpdateKeyModule: React.FC<UpdateKeyModuleProps> = ({ show, onHide, keyData, doorId, roomId, onKeyUpdated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    responsibleParty: '',
    isRestricted: false,
    handoverStatus: 'PENDING',
    status: 'ACTIVE',
    separatedKeys: 0,
    bundles: [{ count: 1 }],
  });

  useEffect(() => {
    if (show && keyData) {
      setFormData({
        id: keyData.id,
        responsibleParty: keyData.responsibleParty || '',
        isRestricted: keyData.isRestricted,
        handoverStatus: keyData.handoverStatus || 'PENDING',
        status: keyData.status,
        separatedKeys: keyData.separatedKeys || 0,
        bundles: keyData.bundles && keyData.bundles.length > 0
          ? keyData.bundles.map(bundle => ({ count: bundle.count }))
          : [{ count: 1 }],
      });
    }
  }, [show, keyData]);

  const handleBundleCountChange = (index: number, value: number) => {
    const updatedBundles = [...formData.bundles];
    updatedBundles[index] = { count: Math.max(1, value) };
    setFormData({ ...formData, bundles: updatedBundles });
  };

  const handleAddBundle = () => {
    setFormData({
      ...formData,
      bundles: [...formData.bundles, { count: 1 }],
    });
  };

  const handleRemoveBundle = (index: number) => {
    setFormData({
      ...formData,
      bundles: formData.bundles.filter((_, i) => i !== index),
    });
  };

  const noOfKeys = (formData.separatedKeys || 0) + formData.bundles.reduce((sum, bundle) => sum + (bundle.count || 0), 0);

  const handleUpdateKey = async () => {
    if (!checkPermission('ROLE_UPDATE_KEY')) {
      showError('You do not have permission to update keys.');
      return;
    }
    if (!formData.id || !keyData) {
      showError('No key selected for update.');
      return;
    }
    if (noOfKeys <= 0) {
      showError('Total keys must be greater than 0.');
      return;
    }
    if (formData.bundles.some(bundle => bundle.count <= 0)) {
      showError('Each bundle must have at least 1 key.');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        id: formData.id,
        doorId: keyData.doorId,
        roomId: keyData.roomId,
        vehicleId: keyData.vehicleId,
        noOfKeys,
        separatedKeys: formData.separatedKeys || 0,
        bundles: formData.bundles.map(bundle => ({ count: bundle.count })) || null,
        responsibleParty: formData.responsibleParty || null,
        isRestricted: formData.isRestricted,
        handoverStatus: formData.handoverStatus || null,
        status: formData.status,
      };
      const res = await apiDefinitions.updateKey(payload);
      if (res.status === 200 && res.data.status === 'S1000') {
        showSuccess("Key updated successfully");
        onKeyUpdated();
        onHide();
      } else {
        showError(res.data?.statusDescription || 'Failed to update key');
      }
    } catch (e: any) {
      showError(`Failed to update key: ${e.response?.data?.statusDescription || e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update Key (ID: {formData.id})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <Form>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group controlId="noOfKeys">
                  <Form.Label>Total Keys</Form.Label>
                  <Form.Control type="number" min="0" value={noOfKeys} readOnly />
                  <small className="text-muted">Auto-calculated as Separated + sum(Bundle counts)</small>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="separatedKeys">
                  <Form.Label>Separated Keys</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.separatedKeys}
                    onChange={(e) => setFormData({ ...formData, separatedKeys: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Label>Bundles</Form.Label>
                {formData.bundles.map((bundle, index) => (
                  <Row key={index} className="mb-2 align-items-center">
                    <Col xs={8}>
                      <Form.Group controlId={`bundle-${index}`}>
                        <Form.Control
                          type="number"
                          min="1"
                          placeholder={`Keys in bundle ${index + 1}`}
                          value={bundle.count}
                          onChange={(e) => handleBundleCountChange(index, parseInt(e.target.value) || 1)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={4}>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveBundle(index)}
                        disabled={formData.bundles.length === 0}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button variant="outline-primary" size="sm" onClick={handleAddBundle} className="mt-2">
                  Add Bundle
                </Button>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group controlId="responsibleParty">
                  <Form.Label>Responsible Party</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.responsibleParty}
                    onChange={(e) => setFormData({ ...formData, responsibleParty: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="handoverStatus">
                  <Form.Label>Handover Status</Form.Label>
                  <Form.Control
                    as="select"
                    value={formData.handoverStatus}
                    onChange={(e) => setFormData({ ...formData, handoverStatus: e.target.value })}
                  >
                    <option value="PENDING">PENDING</option>
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                    <option value="">Not Set</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="status">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="isRestricted">
                  <Form.Check
                    type="checkbox"
                    label="Restricted"
                    checked={formData.isRestricted}
                    onChange={(e) => setFormData({ ...formData, isRestricted: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                className="me-2"
                onClick={onHide}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={handleUpdateKey}
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" /> : 'Update Key'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UpdateKeyModule;